import React, { useCallback, useState } from 'react'
import ViewCheckImageModal from './ViewCheckImageModal'
import VoidACheckModal from './VoidACheckModal'
import EditIssuedCheckModal from './EditIssuedCheckModal'
import DeleteIssuedCheckModal from './DeleteIssuedCheckModal'
import { useSearchResultContext } from '../index.context'
import { MenuProps, notification, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { AuditIcon, DeleteIcon, EditIcon, EyeIcon, GalleryIcon, KeyIcon } from 'src/assets/icons'
import QuickFilter from './QuickFilter'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { useLocation, useNavigate } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import {
  canPerformTransactionAction,
  ETransactionAction,
} from 'src/app/shared/utils/transactionPermissions.util'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { IGetExportData } from 'src/app/shared/components/common/new-table/table-setting.model'
import { settingHeightTableWithFilter } from 'src/app/shared/constants/common.constants'

const ResultTable: React.FC = () => {
  const { t } = useTranslation()
  const { state } = useLocation()
  const navigate = useNavigate()
  const {
    transactionState,
    loading,
    filterState,
    outerTableRef,
    defaultParams,
    getTableName,
    handleSearch,
    handleDownload,
    generateCustomColumnDefs,
  } = useSearchResultContext()
  const { userPermissions } = useRolePermission()

  const [isVoidACheckModalOpen, setIsVoidACheckModalOpen] = useState(false)
  const [isViewCheckImageModalOpen, setIsViewCheckImageModalOpen] = useState(false)
  const [isEditIssuedCheckModalOpen, setIsEditIssuedCheckModalOpen] = useState(false)
  const [isDeleteIssuedCheckModalOpen, setIsDeleteIssuedCheckModalOpen] = useState(false)
  const [checkNumber, setCheckNumber] = useState('')
  const [id, setId] = useState('')
  const [selectedRowData, setSelectedRowData] = useState<any>(null)
  const [api, contextHolder] = notification.useNotification()

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      const canView = userPermissions[ESubModules.ALL_TRANS]?.includes(EPermissions.VIEW_ITEMS)
      const canVoid = canPerformTransactionAction({
        rowData,
        action: ETransactionAction.VOID,
        userPermissions,
        subModule: ESubModules.ALL_TRANS,
        permission: EPermissions.AVOID_ISSUED,
      })
      const canEdit = canPerformTransactionAction({
        rowData,
        action: ETransactionAction.EDIT,
        userPermissions,
        subModule: ESubModules.ADD_NEW_ISSUE_CHK,
        permission: EPermissions.ADD_EDIT_ITEMS,
      })
      const canDelete = canPerformTransactionAction({
        rowData,
        action: ETransactionAction.DELETE,
        userPermissions,
        subModule: ESubModules.ADD_NEW_ISSUE_CHK,
        permission: EPermissions.DELETE_ITEMS,
      })

      return [
        {
          key: '1',
          label: (
            <Typography.Text style={{ fontSize: '14px', opacity: !canView ? 0.4 : 1 }}>
              {t('TransactionResult:actions.viewTransaction')}
            </Typography.Text>
          ),
          icon: <EyeIcon />,
          disabled: !canView,
          onClick: () => {
            navigate(`${menuUrls.viewTransaction}/${rowData?.id}`, { state })
          },
        },
        {
          key: '2',
          label: (
            <Typography.Text style={{ fontSize: '14px', opacity: !canVoid ? 0.4 : 1 }}>
              {t('TransactionResult:actions.voidACheck')}
            </Typography.Text>
          ),
          icon: <KeyIcon />,
          disabled: !canVoid,
          onClick: () => {
            handleVoidACheck(rowData)
          },
        },
        {
          key: '3',
          label: (
            <Typography.Text style={{ fontSize: '14px', opacity: !rowData?.imageFound ? 0.4 : 1 }}>
              {t('TransactionResult:actions.viewCheckImage')}
            </Typography.Text>
          ),
          icon: <GalleryIcon />,
          disabled: !rowData?.imageFound,
          onClick: () => {
            handleViewCheckImage(rowData)
          },
        },
        {
          key: '4',
          label: (
            <Typography.Text style={{ fontSize: '14px', opacity: !canView ? 0.4 : 1 }}>
              {t('TransactionResult:actions.viewAuditHistory')}
            </Typography.Text>
          ),
          icon: <AuditIcon />,
          disabled: !canView,
          onClick: () => {
            navigate(`${menuUrls.viewAuditHistory}/${rowData?.id}`, { state })
          },
        },
        {
          key: '5',
          label: (
            <Typography.Text style={{ fontSize: '14px', opacity: !canEdit ? 0.4 : 1 }}>
              {t('TransactionResult:actions.editIssuedCheck')}
            </Typography.Text>
          ),
          icon: <EditIcon />,
          disabled: !canEdit,
          onClick: () => {
            handleEditIssuedCheck(rowData)
          },
        },
        {
          key: '6',
          label: (
            <Typography.Text style={{ fontSize: '14px', opacity: !canDelete ? 0.4 : 1 }}>
              {t('TransactionResult:actions.deleteIssuedCheck')}
            </Typography.Text>
          ),
          icon: <DeleteIcon />,
          disabled: !canDelete,
          onClick: () => {
            handleDeleteIssuedCheck(rowData)
          },
        },
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  )

  const handleVoidACheck = (rowData: any) => {
    setCheckNumber(rowData?.checkNumber)
    setId(rowData?.id)
    setIsVoidACheckModalOpen(true)
  }

  const onCloseVoidACheckModal = () => {
    setIsVoidACheckModalOpen(false)
  }

  const handleViewCheckImage = (rowData: any) => {
    setIsViewCheckImageModalOpen(true)
    setId(rowData?.id)
    setCheckNumber(rowData?.checkNumber)
  }

  const onCloseViewCheckImageModal = () => {
    setIsViewCheckImageModalOpen(false)
  }

  const handleEditIssuedCheck = (rowData: any) => {
    setSelectedRowData(rowData)
    setIsEditIssuedCheckModalOpen(true)
  }

  const onCloseEditIssuedCheckModal = () => {
    setIsEditIssuedCheckModalOpen(false)
    setSelectedRowData(null)
  }

  const handleDeleteIssuedCheck = (rowData: any) => {
    setCheckNumber(rowData?.checkNumber)
    setId(rowData?.id)
    setIsDeleteIssuedCheckModalOpen(true)
  }

  const onCloseDeleteIssuedCheckModal = () => {
    setIsDeleteIssuedCheckModalOpen(false)
  }

  return (
    <>
      {contextHolder}

      <NewCommonTable
        ref={outerTableRef}
        rowData={transactionState.items}
        total={transactionState.total}
        loading={loading}
        suppressLoadingOverlay
        displayDateFilter
        translationNamespace='TransactionResult:table'
        tableSettingName={getTableName(filterState.selectedFilter)}
        pageName={getTableName(filterState.selectedFilter)}
        actionDropdownItems={getItems}
        handleSearch={handleSearch}
        handleDownload={handleDownload}
        generateCustomColumnDefs={generateCustomColumnDefs}
        defaultParams={defaultParams}
        customFilter={(
          props: Partial<IGetExportData> & {
            loading?: boolean
          },
        ) => <QuickFilter props={props} />}
        settingHeightTable={settingHeightTableWithFilter}
      />


      <VoidACheckModal
        isOpen={isVoidACheckModalOpen}
        onClose={onCloseVoidACheckModal}
        checkNumber={checkNumber}
        id={id}
        notiApi={api}
        onGridReady={() => outerTableRef.current?.handleSearchLogic?.({})}
      />

      <ViewCheckImageModal
        id={id}
        checkNumber={checkNumber}
        isOpen={isViewCheckImageModalOpen}
        onClose={onCloseViewCheckImageModal}
      />

      <EditIssuedCheckModal
        isOpen={isEditIssuedCheckModalOpen}
        onClose={onCloseEditIssuedCheckModal}
        rowData={selectedRowData}
        notiApi={api}
        onGridReady={() => outerTableRef.current?.handleSearchLogic?.({})}
      />

      <DeleteIssuedCheckModal
        isOpen={isDeleteIssuedCheckModalOpen}
        onClose={onCloseDeleteIssuedCheckModal}
        checkNumber={checkNumber}
        id={id}
        notiApi={api}
        onGridReady={() => outerTableRef.current?.handleSearchLogic?.({})}
      />
    </>
  )
}

export default ResultTable
